// src/pages/Templates/SaveTemplateModal.tsx

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  defaultName: string;
  existingNames?: string[];
  isSaving: boolean;
  isUpdate?: boolean;
  fromTab: string | undefined;
  saveascopyinput: boolean;
  showSaveascopyinput: any;
  saveDisabled?: boolean;
  isPrebuilt?: boolean;
}

const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultName,
  existingNames = [],
  isSaving,
  isUpdate = false,
  fromTab,
  saveascopyinput,
  showSaveascopyinput,
  saveDisabled = false,
  isPrebuilt = false,
}) => {
  const [name, setName] = useState(defaultName);
  const [originalName, setOriginalName] = useState(defaultName);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [isNameChanged, setIsNameChanged] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // For "Save as Copy" actions, prefill with the current template name
      const isSavingCopy = isPrebuilt || fromTab === 'templatesGallery' || saveascopyinput;

      if (isSavingCopy) {
        setName(defaultName);
        setOriginalName(defaultName);
        setIsNameChanged(false);
      } else {
        setName(defaultName);
        setOriginalName(defaultName);
        setIsNameChanged(false);
      }
      setError('');
      setTouched(false);
    }
  }, [isOpen, defaultName, isPrebuilt, fromTab, saveascopyinput]);

  useEffect(() => {
    if (touched) {
      validateName(name);
    }
  }, [name, touched]);

  const validateName = (value: string): boolean => {
    const trimmed = value.trim();

    if (!trimmed) {
      setError('Name is required');
      return false;
    }

    const needsUniqueName =
      !isUpdate || saveascopyinput || fromTab === 'templatesGallery' || isPrebuilt;

    // For "Save as Copy", check if name already exists (excluding the current template's own name)
    if (needsUniqueName) {
      // Check if the name exists in the list (case insensitive)
      const isDuplicate = existingNames.some(
        (existingName) => existingName.toLowerCase() === trimmed.toLowerCase()
      );

      if (isDuplicate) {
        setError(`A template with the name "${trimmed}" already exists. Please choose a different name.`);
        return false;
      }
    }

    setError('');
    return true;
  };

  const generateUniqueName = (baseName: string): string => {
    let nameToCheck = baseName.trim();

    const existingNumberMatch = nameToCheck.match(/\s\((\d+)\)$/);
    if (existingNumberMatch) {
      const currentNum = parseInt(existingNumberMatch[1], 10);
      nameToCheck = nameToCheck.replace(/\s\(\d+\)$/, '').trim();
      let counter = currentNum + 1;

      while (existingNames.includes(`${nameToCheck} (${counter})`)) {
        counter++;
      }
      return `${nameToCheck} (${counter})`.trim();
    }

    if (!existingNames.includes(nameToCheck)) {
      return nameToCheck;
    }

    let counter = 1;
    while (existingNames.includes(`${nameToCheck} (${counter})`)) {
      counter++;
    }
    return `${nameToCheck} (${counter})`.trim();
  };

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!validateName(trimmed)) {
      return;
    }

    const needsUniqueName =
      !isUpdate || saveascopyinput || fromTab === 'templatesGallery' || isPrebuilt;

    if (needsUniqueName && existingNames.includes(trimmed)) {
      const uniqueName = generateUniqueName(trimmed);
      setName(uniqueName);
      onSave(uniqueName);
      return;
    }

    onSave(trimmed);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setTouched(true);
    // Check if the name has been changed from the original
    setIsNameChanged(value.trim() !== originalName.trim());
  };

  if (!isOpen) return null;

  // Determine if save button should be disabled
  const isSavingCopy = isPrebuilt || fromTab === 'templatesGallery' || saveascopyinput;

  // For "Save as Copy": disable save if name hasn't changed or there's an error
  // For normal "Update Template": only disable if saving, name is empty, or there's an error
  // We don't use saveDisabled for Update Template because we already validated changes before opening the modal
  const isSaveDisabled = isSavingCopy
    ? isSaving || !name.trim() || !!error || !isNameChanged
    : isSaving || !name.trim() || !!error;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-xl shadow-2xl max-w-md w-full relative'>
        <button
          onClick={
            isUpdate && fromTab !== 'templatesGallery' && saveascopyinput
              ? () => {
                showSaveascopyinput(false);
                onClose();
              }
              : onClose
          }
          disabled={isSaving}
          className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition'
          aria-label='Close modal'
        >
          <FiXComponent size={24} />
        </button>

        <div className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            {isPrebuilt || (isUpdate && fromTab === 'templatesGallery')
              ? 'Save a Copy'
              : isUpdate && fromTab !== 'templatesGallery' && !saveascopyinput
                ? 'Save Template Changes'
                : isUpdate && fromTab !== 'templatesGallery' && saveascopyinput
                  ? 'Save a Copy'
                  : 'Save New Template'}
          </h3>
          <p className='text-sm text-gray-600 mb-5'>
            {isPrebuilt || (isUpdate && fromTab === 'templatesGallery')
              ? 'This will save the template as a copy in your templates.'
              : isUpdate && fromTab !== 'templatesGallery' && !saveascopyinput
                ? "You're editing an existing template. Choose how you'd like to save your changes."
                : isUpdate && fromTab !== 'templatesGallery' && saveascopyinput
                  ? 'This will save the template as a copy.'
                  : 'Choose a name for your template.'}
          </p>

          {/* Show info message for "Save as Copy" when name hasn't changed */}
          {isSavingCopy && !isNameChanged && !error && (
            <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
              <p className='text-sm text-blue-700 flex items-center gap-2'>
                <span>ℹ️</span>
                <span>Please change the template name to save as a copy.</span>
              </p>
            </div>
          )}

          {/* Always show name input for prebuilt templates or when saving as copy */}
          {(isPrebuilt ||
            isUpdate && fromTab === 'templatesGallery' ||
            !isUpdate ||
            (isUpdate && fromTab !== 'templatesGallery' && saveascopyinput)) && (
              <>
                <label className='block text-[12px] font-normal text-gray-600 mb-2'>
                  TEMPLATE NAME <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  value={name}
                  onChange={handleNameChange}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && !isSaveDisabled && handleSubmit()
                  }
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${error ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder='e.g. Black Friday Promo'
                  autoFocus
                  disabled={isSaving}
                />
                {error && (
                  <p className='mt-2 text-sm text-red-600 flex items-start gap-1'>
                    <span className='inline-block mt-0.5'>⚠️</span>
                    <span>{error}</span>
                  </p>
                )}
                {!error && name.trim() && touched && isNameChanged && (
                  <p className='mt-2 text-sm text-green-600'>
                    ✓ Name is available
                  </p>
                )}
              </>
            )}

          <div
            className={`flex ${isUpdate && fromTab !== 'templatesGallery' && saveascopyinput
              ? 'justify-between'
              : 'justify-end'
              } gap-2 mt-6`}
          >
            {isUpdate && fromTab !== 'templatesGallery' && saveascopyinput && (
              <button
                onClick={() => showSaveascopyinput(false)}
                disabled={isSaving}
                className='px-4 py-2 border border-orange-500 text-orange-500 bg-white hover:bg-orange-50 text-sm rounded'
              >
                Back
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={isSaveDisabled}
              className={
                (isUpdate &&
                  fromTab !== 'templatesGallery' &&
                  saveascopyinput) ||
                  (isUpdate && fromTab === 'templatesGallery') ||
                  !isUpdate ||
                  isPrebuilt
                  ? `px-5 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2`
                  : `px-4 py-2 border border-orange-500 text-orange-500 bg-white hover:bg-orange-50 text-sm rounded`
              }
              title={
                isSavingCopy && !isNameChanged
                  ? 'Please change the template name to save as a copy'
                  : error
                    ? 'Please fix the errors above'
                    : 'Save template'
              }
            >
              {isSaving ? (
                <>
                  <svg className='animate-spin h-4 w-4' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                      fill='none'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8v-4a8 8 0 00-8-8z'
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  {isUpdate &&
                    fromTab !== 'templatesGallery' &&
                    !saveascopyinput
                    ? 'Update Template'
                    : isUpdate &&
                      fromTab !== 'templatesGallery' &&
                      saveascopyinput
                      ? 'Save'
                      : 'Save'}{' '}
                </>
              )}
            </button>

            {/* Save as Copy button (only when updating from your templates) */}
            {isUpdate && fromTab !== 'templatesGallery' && !saveascopyinput && (
              <button
                onClick={() => showSaveascopyinput(true)}
                disabled={isSaving}
                className='px-5 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 transition flex items-center gap-2'
              >
                Save a Copy
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveTemplateModal;